import { useCallback, useEffect, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { SubmitHandler, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  CheckBoxCard,
  ContentPagination,
  FormBase,
  type IPaginationMetaProps,
  Logger,
  SelectData,
  TemplatesType,
  groupsTypes,
  templatesTypes,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { Card } from '../../../../components/Card';
import { CenteredText } from '../../../../components/CenteredText';
import { FormPaginationModal } from '../../../../components/FormPaginationModal';
import { VALIDATION_LIMITS } from '../../../../constants/ValidationRules';
import { useClients } from '../../../../hooks/useCompanies';
import { useDragDrop } from '../../../../hooks/useDragDrop';
import {
  type ICreateTemplateGroupProps,
  type IPaginationTemplatesDTO,
  type ITemplateGroupDTO,
  type ITemplatePageDTO,
  type IUpdateTemplateGroupProps,
  getTemplateGroupPages,
  getTemplatePagesByClientId,
} from '../../../../services/templates';
import {
  type GroupTemplateNamingSchema,
  groupTemplateNamingSchema,
} from '../../../TemplateCourses/templateSchema';
import { Loading, LoadingContainer } from '../../styles';
import { SearchTemplates } from '../SearchTemplates';
import { TemplateCard } from '../TemplateCard';
import {
  CheckBoxGroupCustom,
  Content,
  DroppableContainer,
  MainContainer,
  TitleText,
} from './styles';

export enum GroupTemplateModalStep {
  NAMING = 'naming',
  SELECTING_TEMPLATES = 'selectingTemplates',
}

export interface IGroupTemplateModalProps {
  groupTemplateToUpdate: ITemplateGroupDTO | null;
  isLoadingAccept: boolean;
  isUserAdmin: boolean;
  isUserManager: boolean;

  onAcceptCreate?: (groupTemplate: ICreateTemplateGroupProps) => void;
  onAcceptUpdate?: (groupTemplate: IUpdateTemplateGroupProps) => void;
  onDecline: () => void;
}

export interface ITemplatePage extends ITemplatePageDTO {
  index?: number;
  selected?: boolean;
}

export interface ITemplatePageWithOrder extends ITemplatePage {
  order?: number;
}

interface IDroppableProps {
  droppableId: string;
  from: number;
  to: number;
}

export const GroupTemplateModal = ({
  groupTemplateToUpdate,
  isLoadingAccept,
  isUserAdmin,
  isUserManager,
  onAcceptCreate,
  onAcceptUpdate,
  onDecline,
}: IGroupTemplateModalProps): JSX.Element => {
  const { selectedClient } = useClients();

  const isSelectOpen = useRef(new Set());
  const { addDragDropListener, removeDragDropListener } = useDragDrop();

  const refFormModalContent = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<GroupTemplateModalStep>(
    GroupTemplateModalStep.NAMING
  );

  const groupTemplateNamingForm = useForm<GroupTemplateNamingSchema>({
    resolver: zodResolver(groupTemplateNamingSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      title: '',
      templateOptions: TemplatesType.MY_TEMPLATES,
    },
  });

  const {
    handleSubmit: handleSubmitNaming,
    formState: { errors: namingErrors, isValid: isNamingValid },
    setValue: setValueNaming,
    reset: resetNaming,
    watch,
  } = groupTemplateNamingForm;

  const values = watch();

  const [templates, setTemplates] = useState<Map<number, ITemplatePage>>(
    new Map()
  );
  const [templatesPaginationMeta, setTemplatesPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);
  const [selectedTemplates, setSelectedTemplates] = useState<
    ITemplatePageWithOrder[]
  >([]);
  const [selectedTemplatesType, setSelectedTemplatesType] = useState<number>(
    !isUserAdmin ? TemplatesType.MY_TEMPLATES : TemplatesType.GLOBAL_TEMPLATES
  );
  const [templatesSearchData, setTemplatesSearchData] = useState('');
  const lastTemplatesSearchData = useRef('');
  const [isSearchingTemplates, setIsSearchingTemplates] = useState(false);

  const courseTemplatesOptionsFiltered = groupsTypes;

  const courseTemplateOptionsStaff = groupsTypes.filter(
    (i) => i.key === TemplatesType.MY_TEMPLATES
  );

  const courseTemplateOptionsManager = groupsTypes.filter(
    (i) =>
      i.key === TemplatesType.MY_TEMPLATES ||
      i.key === TemplatesType.SHARE_TEMPLATES
  );

  const templatesOptionsFiltered = templatesTypes.filter(
    (i) => i.key !== TemplatesType.BUILD_SCRATCH
  );

  const templateOptionsStaff = templatesTypes.filter(
    (i) => i.key === TemplatesType.MY_TEMPLATES
  );

  const templateOptionsManager = templatesTypes.filter(
    (i) =>
      i.key === TemplatesType.MY_TEMPLATES ||
      i.key === TemplatesType.SHARE_TEMPLATES
  );

  const DroppableId = 'template-course-droppable';

  useEffect(() => {
    if (groupTemplateToUpdate) {
      resetNaming({
        title: groupTemplateToUpdate.title,
        templateOptions: groupTemplateToUpdate.templateOptions.id,
      });

      void getTemplateGroupPages({
        templateGroupId: groupTemplateToUpdate.id,
      }).then((response) => {
        const templatesWithOrder = response.data.map((template) => {
          const order = template.courseTemplatePageOrders?.find(
            (groupsOrder) => {
              return groupsOrder.courseTemplate.id === groupTemplateToUpdate.id;
            }
          );

          return {
            ...template,
            selected: true,
            order: order ? order.pageOrder : 0,
          };
        });

        templatesWithOrder.sort((a, b) => {
          return a.order - b.order;
        });

        setSelectedTemplates(templatesWithOrder);
      });
    } else {
      resetNaming({
        title: '',
        templateOptions: TemplatesType.MY_TEMPLATES,
      });
    }
  }, [groupTemplateToUpdate, resetNaming]);

  const handleNextStep: SubmitHandler<GroupTemplateNamingSchema> = useCallback(
    (_data) => {
      setCurrentStep(GroupTemplateModalStep.SELECTING_TEMPLATES);
    },
    []
  );

  const handleDeclineAddCourseTemplate = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetNaming({
        title: '',
        templateOptions: TemplatesType.MY_TEMPLATES,
      });
      setSelectedTemplates([]);
      setTemplates(new Map());
      setCurrentStep(GroupTemplateModalStep.NAMING);
      onDecline?.();
    }
  }, [onDecline, resetNaming]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const handleVerifyAction = useCallback(() => {
    const title = values.title ?? '';
    const templateOptions =
      values.templateOptions ?? TemplatesType.MY_TEMPLATES;

    if (groupTemplateToUpdate) {
      if (!onAcceptUpdate) {
        Logger.error(
          'Could not find the required operation to update the group template'
        );
        return;
      }

      onAcceptUpdate({
        id: groupTemplateToUpdate.id,
        title,
        templateOptions,
        pageTemplates: selectedTemplates.map((template) => template.id),
      });
    } else {
      if (!onAcceptCreate) {
        Logger.error(
          'Could not find the required operation to create the group template'
        );
        return;
      }

      onAcceptCreate({
        title,
        client: selectedClient?.id ?? 0,
        templateOptions,
        pageTemplates: selectedTemplates.map((template) => template.id),
      });
    }
  }, [
    values.title,
    values.templateOptions,
    groupTemplateToUpdate,
    onAcceptUpdate,
    selectedTemplates,
    onAcceptCreate,
    selectedClient?.id,
  ]);

  const handlePreviousStep = useCallback(() => {
    setCurrentStep(GroupTemplateModalStep.NAMING);
  }, []);

  const searchCourseTemplateData = useCallback(
    async (incrementPage = false) => {
      setIsSearchingTemplates(true);

      let currentPage = 1;

      if (incrementPage && templatesPaginationMeta) {
        currentPage = templatesPaginationMeta.page + 1;
      }

      await getTemplatePagesByClientId({
        clientId: selectedClient?.id ?? 0,
        templateOptionsId: selectedTemplatesType,
        page: currentPage,
        limit: 15,
        filter: templatesSearchData,
      })
        .then((data: IPaginationTemplatesDTO) => {
          const newTemplates = new Map<number, ITemplatePage>(
            incrementPage ? templates : new Map()
          );

          data.data.forEach((template) => {
            newTemplates.set(template.id, {
              ...template,
              selected: false,
            });
          });

          selectedTemplates.forEach((template) => {
            if (!newTemplates.has(template.id)) {
              newTemplates.set(template.id, { ...template, selected: true });
            } else {
              const currentTemplate = newTemplates.get(template.id);

              if (currentTemplate) {
                newTemplates.set(template.id, {
                  ...currentTemplate,
                  selected: true,
                });
              }
            }
          });

          setTemplates(newTemplates);
          setTemplatesPaginationMeta(data.meta);
        })
        .catch((err) => {
          Logger.error('Failed to search the Templates', err);
        })
        .finally(() => {
          setIsSearchingTemplates(false);
        });
    },
    [
      templatesPaginationMeta,
      selectedClient?.id,
      selectedTemplatesType,
      templatesSearchData,
      templates,
      selectedTemplates,
    ]
  );

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (templatesSearchData.trim() !== lastTemplatesSearchData.current) {
      lastTemplatesSearchData.current = templatesSearchData;

      if (templatesSearchData.trim() === '') {
        void searchCourseTemplateData();
      } else {
        timer = setTimeout(() => {
          void searchCourseTemplateData();
        }, 1000);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [templatesSearchData]);

  useEffect(() => {
    if (currentStep === GroupTemplateModalStep.SELECTING_TEMPLATES) {
      void searchCourseTemplateData();
    }
  }, [currentStep, selectedTemplatesType]);

  const handleSelectTemplate = (data: ITemplatePage) => {
    setSelectedTemplates((prevSelectedTemplates) => {
      prevSelectedTemplates.push({
        ...data,
        order: prevSelectedTemplates.length,
      });

      return prevSelectedTemplates;
    });

    setTemplates((prev) => {
      const updatedMap = new Map<number, ITemplatePage>(prev);

      if (updatedMap.has(data.id)) {
        const template = updatedMap.get(data.id);

        if (template) {
          updatedMap.set(data.id, { ...template, selected: true });
        }
      }
      return updatedMap;
    });
  };

  const handleDeselectTemplate = (data: ITemplatePage) => {
    setSelectedTemplates((prevSelectedTemplates) => {
      const itemIndex = prevSelectedTemplates.findIndex((template) => {
        return template.id === data.id;
      });

      if (itemIndex === -1) {
        return prevSelectedTemplates;
      }

      const updatedTemplates = [
        ...prevSelectedTemplates.slice(0, itemIndex),
        ...prevSelectedTemplates.slice(itemIndex + 1),
      ];

      return updatedTemplates;
    });

    setTemplates((prevTeste) => {
      const updatedMap = new Map(prevTeste);

      if (updatedMap.has(data.id)) {
        const template = updatedMap.get(data.id);

        if (template) {
          updatedMap.set(data.id, { ...template, selected: false });
        }
      }
      return updatedMap;
    });
  };

  useEffect(() => {
    const handleValueChange = ({ droppableId, from, to }: IDroppableProps) => {
      if (droppableId !== DroppableId) return;

      setSelectedTemplates((prevSelectedTemplates) => {
        const sortedTemplates = prevSelectedTemplates.sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );

        const [movedItem] = sortedTemplates.splice(from, 1);

        sortedTemplates.splice(to, 0, movedItem);

        const updatedTemplates: ITemplatePageWithOrder[] =
          sortedTemplates.reduce<ITemplatePageWithOrder[]>(
            (acc, template, index) => {
              acc.push({ ...template, order: index + 1 });
              return acc;
            },
            []
          );

        return updatedTemplates;
      });
    };

    addDragDropListener(DroppableId, handleValueChange);

    return () => {
      removeDragDropListener(DroppableId);
    };
  }, [addDragDropListener, removeDragDropListener, DroppableId]);

  const formLayoutParams = {
    naming: {
      acceptText: 'Next',
      declineText: 'Cancel',
      onAccept: handleSubmitNaming(handleNextStep),
      onDecline,
      fullWidth: false,
      vhHeight: '',
    },
    selectingTemplates: {
      acceptText: groupTemplateToUpdate
        ? 'Update Course Template'
        : 'Create Course Template',
      declineText: 'Back',
      onAccept: handleVerifyAction,
      onDecline: handlePreviousStep,
      fullWidth: true,
      vhHeight: '70vh',
    },
  };

  return (
    <FormPaginationModal
      mainText={
        groupTemplateToUpdate ? 'Update Course Template' : 'New Course Template'
      }
      dataCy="title-courseTemplate"
      acceptText={formLayoutParams[currentStep].acceptText}
      declineText={formLayoutParams[currentStep].declineText}
      onAccept={formLayoutParams[currentStep].onAccept}
      onDecline={formLayoutParams[currentStep].onDecline}
      onOpenChange={handleDeclineAddCourseTemplate}
      fullWidth={formLayoutParams[currentStep].fullWidth}
      vhHeight={formLayoutParams[currentStep].vhHeight}
      disabled={
        (currentStep === GroupTemplateModalStep.NAMING && !isNamingValid) ||
        (currentStep === GroupTemplateModalStep.SELECTING_TEMPLATES &&
          selectedTemplates.length === 0)
      }
      open
      loading={isLoadingAccept}
      refContent={refFormModalContent}
    >
      {currentStep === GroupTemplateModalStep.NAMING && (
        <FormBase.Provider {...groupTemplateNamingForm}>
          <FormBase.Content data-cy="group-template-modal-title-form">
            <FormBase.InputContent
              dataCy="label-courseTemplate"
              label="Course Template Name"
              filled={!!values.title}
              inputRef="group-name"
              errorMessage={namingErrors.title?.message}
            >
              <FormBase.InputText
                dataCy="input-courseTemplate"
                id="group-name"
                name="title"
                onChange={(e) => {
                  const value = e.target.value.replace(/\s\s+/g, ' ');
                  if (value.length <= VALIDATION_LIMITS.TEMPLATE.NAME_MAX) {
                    setValueNaming('title', value, { shouldValidate: true });
                  }
                }}
                required
                type="text"
                maxLength={VALIDATION_LIMITS.TEMPLATE.NAME_MAX}
              />
            </FormBase.InputContent>
            <FormBase.InputContent
              dataCy="label-optionsCourseTemplate"
              filled
              label="Course Template Options"
              inputRef="group-type"
              isSelectData
              value={String(
                values.templateOptions ?? TemplatesType.MY_TEMPLATES
              )}
              errorMessage={namingErrors.templateOptions?.message}
            >
              <SelectData
                defaultValue={String(
                  values.templateOptions ?? TemplatesType.MY_TEMPLATES
                )}
                data={
                  isUserAdmin
                    ? courseTemplatesOptionsFiltered
                    : isUserManager
                      ? courseTemplateOptionsManager
                      : courseTemplateOptionsStaff
                }
                onValueChange={(id) => {
                  setValueNaming('templateOptions', Number(id), {
                    shouldValidate: true,
                  });
                }}
                value={String(values.templateOptions)}
                dataCy="select-optionsCourseTemplate"
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'courseTemplate');
                }}
              />
            </FormBase.InputContent>
          </FormBase.Content>
        </FormBase.Provider>
      )}

      {currentStep === GroupTemplateModalStep.SELECTING_TEMPLATES && (
        <>
          <TitleText data-cy="description-selectTemplates">
            Select the option of template you would like to use.
            {selectedTemplates.length > 0 &&
              ` (${selectedTemplates.length} selected)`}
          </TitleText>

          <MainContainer>
            <SearchTemplates
              dataCyPrefix="input-searchTemplate"
              labelPrefix="Template"
              templateTypeData={
                isUserAdmin
                  ? templatesOptionsFiltered
                  : isUserManager
                    ? templateOptionsManager
                    : templateOptionsStaff
              }
              maxLength={VALIDATION_LIMITS.PAGE.SEARCH_MAX}
              currentTemplateType={selectedTemplatesType}
              currentSearch={templatesSearchData}
              onChangeTemplateType={(type) => {
                setTemplatesSearchData('');
                setSelectedTemplatesType(type);
              }}
              onChangeSearchData={setTemplatesSearchData}
              onClearSearchData={() => {
                setTemplatesSearchData('');
              }}
              handleOpenChange={handleOpenChange}
              isSearching={isSearchingTemplates}
            />

            <Content>
              <ContentPagination dataCy="group-template-modal-content-templates">
                <CheckBoxGroupCustom
                  type="multiple"
                  dataCy="group-template-modal-checkboxgroup-templates"
                >
                  {templates.size === 0 && !isSearchingTemplates && (
                    <CenteredText
                      message="No templates found"
                      dataCy="no-templates-found"
                    />
                  )}

                  {templates.size === 0 && isSearchingTemplates && (
                    <LoadingContainer data-cy="loading-templates-container">
                      <Loading dataCy="loading-templates-label" />
                    </LoadingContainer>
                  )}

                  {templates.size > 0 && (
                    <InfiniteScroll
                      height={200}
                      style={{
                        overflow: 'auto',
                        maxHeight: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                      }}
                      dataLength={templates.size} // This is important field to render the next data
                      next={async () => {
                        await searchCourseTemplateData(true);
                      }}
                      hasMore={
                        templates.size < (templatesPaginationMeta?.total ?? 0)
                      }
                      loader={
                        <LoadingContainer data-cy="loading-templates-container">
                          <Loading dataCy="loading-templates-label" />
                        </LoadingContainer>
                      }
                      endMessage={
                        <CenteredText
                          message="You have seen it all"
                          dataCy="pagination-end-group-templates"
                        />
                      }
                    >
                      {Array.from(templates.values()).map((template) => {
                        return (
                          <CheckBoxCard
                            key={`group-template-template-${template.id}-checkbox`}
                            dataCy={`template-card-${template.id}-container`}
                            value={template.id.toString()}
                            onClick={() => {
                              handleSelectTemplate(template);
                            }}
                            disabled={template.selected}
                            defaultChecked={template.selected}
                            data-state={
                              template.selected === true ? 'on' : 'off'
                            }
                            isNotButton
                          >
                            <TemplateCard
                              templateData={template}
                              showIcon={false}
                            />
                          </CheckBoxCard>
                        );
                      })}
                    </InfiniteScroll>
                  )}
                </CheckBoxGroupCustom>
              </ContentPagination>
              <ContentPagination dataCy="group-template-modal-content-selected-templates">
                {selectedTemplates.length === 0 && (
                  <CenteredText
                    message="No templates selected"
                    dataCy="no-templates-selected"
                  />
                )}

                {selectedTemplates.length > 0 && (
                  <CheckBoxGroupCustom
                    type="multiple"
                    dataCy="group-template-modal-checkboxgroup-selected-templates"
                  >
                    <ContentPagination dataCy="group-template-modal-content-selected-templates">
                      {/* @ts-expect-error - react-beautiful-dnd type compatibility issue */}
                      <Droppable
                        data-cy="group-template-droppable-cards"
                        isDropDisabled={isSearchingTemplates}
                        droppableId={DroppableId}
                      >
                        {(provided) => (
                          <DroppableContainer
                            data-cy="group-template-droppable-cards-container"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {selectedTemplates.map((template, index) => {
                              return (
                                <Card
                                  dataCy={
                                    'group-template-selected-template-card-' +
                                    template.id.toString()
                                  }
                                  key={
                                    'group-template-selected-template-card-' +
                                    template.id.toString()
                                  }
                                  draggableId={template.id}
                                  index={index}
                                  refViewPort={refFormModalContent}
                                  draggable
                                >
                                  <CheckBoxCard
                                    key={`group-template-selected-template-${template.id}-checkbox`}
                                    dataCy={`group-template-selected-template-${template.id}-checkbox`}
                                    value={template.id.toString()}
                                    onClick={() => {
                                      handleDeselectTemplate(template);
                                    }}
                                    data-state="on"
                                    isNotButton
                                  >
                                    <TemplateCard
                                      templateData={template}
                                      showIcon={false}
                                    />
                                  </CheckBoxCard>
                                </Card>
                              );
                            })}
                            {provided.placeholder}
                          </DroppableContainer>
                        )}
                      </Droppable>
                    </ContentPagination>
                  </CheckBoxGroupCustom>
                )}
              </ContentPagination>
            </Content>
          </MainContainer>
        </>
      )}
    </FormPaginationModal>
  );
};
